import {
  Glass,
  PillChip,
  Dot,
  DisplayText,
  BodyText,
  alpha,
  borderAlpha,
} from "../../components/mobile/primitives";

/** Skills tab showing installed skills with a newly synced skill needing data */
export function FlowSkillsTabWithNew() {
  return (
    <div style={{ padding: "8px 16px 0", display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Header */}
      <div style={{ paddingLeft: 4, display: "flex", flexDirection: "column", gap: 2 }}>
        <DisplayText size={28}>Skills</DisplayText>
        <BodyText
          size={9.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ lineHeight: 1.45 }}
        >
          Analyses and assistants built around your vault.
        </BodyText>
      </div>

      {/* Category chips */}
      <div style={{ display: "flex", gap: 6, overflow: "hidden" }}>
        <PillChip active color="var(--color-kin-green-soft)">
          All
        </PillChip>
        <PillChip>Health</PillChip>
        <PillChip>Sleep</PillChip>
        <PillChip>Labs</PillChip>
      </div>

      {/* Data gap alert — warm amber, matches onboarding integration style */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 12px",
          borderRadius: 14,
          background: "rgba(255,248,225,0.6)",
          border: "1px solid rgba(240,184,0,0.22)",
          backdropFilter: "blur(12px)",
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 14,
            background: "rgba(255,208,40,0.15)",
            border: "1px solid rgba(240,184,0,0.22)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <BodyText size={12} color="var(--color-kin-gold-dark)" weight={700}>
            !
          </BodyText>
        </div>
        <div style={{ flex: 1 }}>
          <BodyText size={10} color="var(--color-kin-gold-dark)" weight={700}>
            Sleep Deep Dive needs more data
          </BodyText>
          <BodyText size={8.5} color="var(--color-kin-muted)" weight={400}>
            1 of 2 data sources ready.
          </BodyText>
        </div>
        <div
          style={{
            padding: "4px 12px",
            borderRadius: 20,
            background: "rgba(255,208,40,0.12)",
            border: "1px solid rgba(240,184,0,0.22)",
          }}
        >
          <BodyText size={9} color="var(--color-kin-gold-dark)" weight={700}>
            Set up
          </BodyText>
        </div>
      </div>

      {/* Explore Marketplace — simple link card */}
      <Glass style={{ borderRadius: 14 }}>
        <div style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <BodyText size={10.5} weight={700}>
              Explore Marketplace
            </BodyText>
            <BodyText size={8.5} color="var(--color-kin-muted)" weight={400}>
              Discover skills built by health experts
            </BodyText>
          </div>
          <svg
            width="8"
            height="12"
            viewBox="0 0 8 12"
            fill="none"
            stroke="var(--color-kin-olive-light)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M1.5 1l5 5-5 5" />
          </svg>
        </div>
      </Glass>

      {/* Installed skill — ready */}
      <div
        style={{
          padding: "12px 14px",
          borderRadius: 14,
          background: "linear-gradient(135deg, rgba(91,122,102,0.06), rgba(91,122,102,0.02))",
          border: "1px solid rgba(91,122,102,0.15)",
          backdropFilter: "blur(12px)",
          display: "flex",
          flexDirection: "column",
          gap: 7,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Dot color="var(--color-kin-green-soft)" size={8} />
          <BodyText size={11} weight={700}>
            Health Dashboard
          </BodyText>
          <div
            style={{
              marginLeft: "auto",
              padding: "2px 8px",
              borderRadius: 10,
              background: alpha("var(--color-kin-green-soft)", "18"),
              border: borderAlpha("var(--color-kin-green-soft)", "40"),
            }}
          >
            <BodyText size={8} color="var(--color-kin-green-soft)" weight={700}>
              Ready
            </BodyText>
          </div>
        </div>
        <BodyText
          size={9.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ lineHeight: 1.45 }}
        >
          Comprehensive overview of your health metrics and trends.
        </BodyText>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 2,
          }}
        >
          <BodyText size={8.5} color="var(--color-kin-muted)" weight={400}>
            Updated 2h ago
          </BodyText>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 12px",
              borderRadius: 20,
              background: "rgba(91,122,102,0.08)",
              border: "1px solid rgba(91,122,102,0.18)",
            }}
          >
            <BodyText size={9} color="var(--color-kin-green-soft)" weight={700}>
              Open
            </BodyText>
            <BodyText size={9} color="var(--color-kin-green-soft)" weight={300}>
              ›
            </BodyText>
          </div>
        </div>
      </div>

      {/* New installed skill — needs setup, lavender accent */}
      <div
        style={{
          padding: "12px 14px",
          borderRadius: 14,
          background: "linear-gradient(135deg, rgba(165,144,255,0.06), rgba(165,144,255,0.02))",
          border: "1px solid rgba(165,144,255,0.15)",
          backdropFilter: "blur(12px)",
          display: "flex",
          flexDirection: "column",
          gap: 7,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Dot color="var(--color-kin-lavender)" size={8} />
          <BodyText size={11} weight={700}>
            Sleep Deep Dive
          </BodyText>
          <div
            style={{
              marginLeft: "auto",
              padding: "2px 8px",
              borderRadius: 10,
              background: "rgba(255,138,77,0.10)",
            }}
          >
            <BodyText size={8} color="#ff8a4d" weight={700}>
              Needs data
            </BodyText>
          </div>
        </div>
        <BodyText
          size={9.5}
          color="var(--color-kin-muted)"
          weight={400}
          style={{ lineHeight: 1.45 }}
        >
          Analyse 30 nights of sleep data for patterns and recovery.
        </BodyText>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginTop: 2,
          }}
        >
          <BodyText size={8.5} color="var(--color-kin-muted)" weight={400}>
            Just installed
          </BodyText>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 12px",
              borderRadius: 20,
              background: "rgba(255,138,77,0.08)",
              border: "1px solid rgba(255,138,77,0.18)",
            }}
          >
            <BodyText size={9} color="#ff8a4d" weight={700}>
              Set up
            </BodyText>
            <BodyText size={9} color="#ff8a4d" weight={300}>
              ›
            </BodyText>
          </div>
        </div>
      </div>
    </div>
  );
}
